import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';

import useScreen from '@lyearn/core/hooks/useScreen';
import { cn } from '@lyearn/style';
import { Drawer, Stack } from '@lyearn/ui';
import PageLayout from '@/components/PageLayout';
import { RouteChromeProvider, useRouteChromeOverrides } from '@/components/RouteChrome';
import { useSidebarCollapse } from '@/components/SidebarCollapse';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { legacyAdminSidebarStyles } from '@/modules/corporate/modules/admin/components/LegacySidebar/style';
import {
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_RAIL_WIDTH,
} from '@/modules/corporate/modules/admin/components/Sidebar/style';
import type { SidebarItems } from '@/modules/corporate/modules/admin/hooks/useSidebar';
import useUnifiedSidebarItems from '@/modules/corporate/navigation/hooks/useUnifiedSidebarItems';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { UserStatusEnum } from '@/types/schema';

const Content = dynamic(
  () => import('@/modules/corporate/modules/admin/components/Admin/Content'),
  {
    ssr: false,
  },
);
const LegacySidebar = dynamic(
  () => import('@/modules/corporate/modules/admin/components/LegacySidebar'),
  { ssr: false },
);
const Sidebar = dynamic(() => import('@/modules/corporate/modules/admin/components/Sidebar'), {
  ssr: false,
});
const PagePalette = dynamic(() => import('@/modules/corporate/navigation/components/PagePalette'), {
  ssr: false,
});

interface AppShellProps {
  children: React.ReactNode;
  showSidebarByDefault?: boolean;
  legacySidebarItems?: SidebarItems;
}

interface ShellProps {
  children: React.ReactNode;
  showSidebar: boolean;
}

function LegacyAdminShell(props: ShellProps & { sidebarItems: SidebarItems }) {
  const { children, showSidebar, sidebarItems } = props;
  const { [FeatureFlags.ShowUserProfile]: showUserProfile } = useFeatureFlags();

  return (
    <Stack className="flex-1">
      {showSidebar ? (
        <LegacySidebar
          className={cn('border-divider border-e', legacyAdminSidebarStyles, {
            'pt-50': !showUserProfile,
          })}
          showUserProfile={showUserProfile}
          sidebarItems={sidebarItems}
        />
      ) : null}
      <Content>{children}</Content>
    </Stack>
  );
}

function UnifiedShell(props: ShellProps) {
  const { children, showSidebar } = props;
  const isAdminRoute = useAdminRoute();
  // the built-in nav plus the admin's custom links and custom pages
  const { sidebarItems, isLoading: isNavLoading } = useUnifiedSidebarItems();
  const { isCollapsed, toggle, isMobileNavOpen, closeMobileNav } = useSidebarCollapse();
  const { isMobile } = useScreen();
  const { [FeatureFlags.ShowUserProfile]: showUserProfile } = useFeatureFlags();

  // The drawer only opens from the mobile header's trigger, but if the
  // viewport grows past mobile while it's open (e.g. rotating a tablet), it
  // has no other cue to close.
  useEffect(() => {
    if (!isMobile) closeMobileNav();
  }, [isMobile, closeMobileNav]);

  const pageContent = <PageLayout>{children}</PageLayout>;
  const content = isAdminRoute ? <Content>{pageContent}</Content> : pageContent;

  if (!showSidebar) {
    return <>{content}</>;
  }

  return (
    <Stack className="flex-1">
      {isMobile ? (
        // Mobile — slide-in drawer opened from the mobile header's menu button
        <Drawer
          PaperProps={{ style: { width: SIDEBAR_EXPANDED_WIDTH, maxWidth: '85vw' } }}
          anchor="left"
          open={isMobileNavOpen}
          onClose={closeMobileNav}>
          <Sidebar
            logoOnlyHeader
            collapsed={false}
            loading={isNavLoading}
            showUserProfile={showUserProfile}
            sidebarItems={sidebarItems}
          />
        </Drawer>
      ) : (
        <aside
          className="sticky top-0 z-50 h-screen flex-shrink-0 self-start transition-[width] duration-300 ease-in-out"
          style={{ width: isCollapsed ? SIDEBAR_RAIL_WIDTH : SIDEBAR_EXPANDED_WIDTH }}>
          <Sidebar
            logoOnlyHeader
            collapsed={isCollapsed}
            loading={isNavLoading}
            showUserProfile={showUserProfile}
            sidebarItems={sidebarItems}
            onToggleCollapse={toggle}
          />
        </aside>
      )}
      <Stack className="min-w-0 flex-1" direction="col">
        {content}
      </Stack>
      <PagePalette />
    </Stack>
  );
}

function AppShell(props: AppShellProps) {
  const { children, legacySidebarItems, showSidebarByDefault = true } = props;
  const {
    ui: { isUnifiedSidebar },
  } = useAppConfig();
  const { sidebar: sidebarOverride } = useRouteChromeOverrides();
  const user = useUserContext();

  // Routed through `showSidebar` so the mobile drawer and the header's menu
  // trigger (which reads `chrome.sidebar`) go with it.
  const emailNotVerified = user.status === UserStatusEnum.EmailNotVerified;
  const showSidebar = emailNotVerified ? false : sidebarOverride ?? showSidebarByDefault;

  if (!isUnifiedSidebar) {
    return legacySidebarItems ? (
      <LegacyAdminShell showSidebar={showSidebar} sidebarItems={legacySidebarItems}>
        {children}
      </LegacyAdminShell>
    ) : (
      <>{children}</>
    );
  }

  if (!user.id) {
    return <>{children}</>;
  }

  return (
    <RouteChromeProvider chrome={{ sidebar: showSidebar }}>
      <UnifiedShell showSidebar={showSidebar}>{children}</UnifiedShell>
    </RouteChromeProvider>
  );
}

export default AppShell;
